/* eslint-disable class-methods-use-this */
/* eslint-disable no-underscore-dangle */
import AccountTreeIcon from '@mui/icons-material/AccountTreeRounded';
import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  ElementFormatType,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  Spread,
} from 'lexical';

import { BlockWithAlignableContents } from '@lexical/react/LexicalBlockWithAlignableContents';
import {
  DecoratorBlockNode,
  SerializedDecoratorBlockNode,
} from '@lexical/react/LexicalDecoratorBlockNode';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import useDecisionTreePreviewContext from '../../../../App/Contexts/DecisionTree/DecisionTreePreviewContext';
import useDecisionTreeRunContext from '../../../../App/Contexts/DecisionTree/DecisionTreeRunContext/useDecisionTreeRunContext';
import { useGetDecisionTreeQuery } from '../../../../generated/core-api/graphql-react-query';
import StyledBox from './styles';

type DecisionTreeLinkComponentProps = Readonly<{
  className: Readonly<{
    base: string;
    focus: string;
  }>;
  format: ElementFormatType | null;
  nodeKey: NodeKey;
  decisionTreeId: string;
}>;

function convertDecisionTreeLinkElement(
  domNode: HTMLDivElement,
): DOMConversionOutput | null {
  const id = domNode.getAttribute('data-lexical-decision-tree-link-id');
  if (id) {
    const node = $createDecisionTreeLinkNode(id);
    return { node };
  }
  return null;
}

function DecisionTreeLinkComponent({
  className,
  format,
  nodeKey,
  decisionTreeId,
}: DecisionTreeLinkComponentProps) {
  const { t } = useTranslation();
  const decisionTree = useGetDecisionTreeQuery({ input: { id: decisionTreeId } });
  const { start } = useDecisionTreeRunContext();
  const isPreview = useDecisionTreePreviewContext();

  return (
    <BlockWithAlignableContents
      className={className}
      format={format}
      nodeKey={nodeKey}
    >
      <StyledBox
        className={clsx({ disabled: !!decisionTree.data?.getDecisionTree.deletedAt })}
        onClick={() => {
          if (!decisionTree.data?.getDecisionTree.deletedAt) { start(decisionTreeId, undefined, isPreview); }
        }}
      >
        <div>
          <AccountTreeIcon />
        </div>
        <div>
          {t('decisionTree.tree.editor.decision-tree-link')}
        </div>
        <strong className={clsx({ deleted: !!decisionTree.data?.getDecisionTree.deletedAt })}>
          {decisionTree.data?.getDecisionTree?.name}
        </strong>
      </StyledBox>
    </BlockWithAlignableContents>
  );
}

export type SerializedDecisionTreeLinkNode = Spread<
  {
    id: string;
    type: 'decision-tree-link';
    version: 1;
  },
  SerializedDecoratorBlockNode
>;

export class DecisionTreeLinkNode extends DecoratorBlockNode {
  __id: string;

  static getType(): string {
    return 'decision-tree-link';
  }

  static clone(node: DecisionTreeLinkNode): DecisionTreeLinkNode {
    return new DecisionTreeLinkNode(node.__id, node.__format, node.__key);
  }

  static importJSON(serializedNode: SerializedDecisionTreeLinkNode): DecisionTreeLinkNode {
    const node = $createDecisionTreeLinkNode(serializedNode.id);
    node.setFormat(serializedNode.format);
    return node;
  }

  exportJSON(): SerializedDecisionTreeLinkNode {
    return {
      ...super.exportJSON(),
      id: this.getId(),
      type: 'decision-tree-link',
      version: 1,
    };
  }

  static importDOM(): DOMConversionMap<HTMLDivElement> | null {
    return {
      div: (domNode: HTMLDivElement) => {
        if (!domNode.hasAttribute('data-lexical-decision-tree-link-id')) {
          return null;
        }
        return {
          conversion: convertDecisionTreeLinkElement,
          priority: 2,
        };
      },
    };
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('div');
    element.setAttribute('data-lexical-decision-tree-link-id', this.__id);
    return { element };
  }

  constructor(id: string, format?: ElementFormatType, key?: NodeKey) {
    super(format, key);
    this.__id = id;
  }

  getId(): string {
    return this.__id;
  }

  decorate(editor: LexicalEditor, config: EditorConfig): JSX.Element {
    const embedBlockTheme = config.theme.embedBlock || {};
    const className = {
      base: embedBlockTheme.base || '',
      focus: embedBlockTheme.focus || '',
    };
    return (
      <DecisionTreeLinkComponent
        className={className}
        format={this.__format}
        nodeKey={this.getKey()}
        decisionTreeId={this.__id}
      />
    );
  }

  isTopLevel(): true {
    return true;
  }
}

export function $createDecisionTreeLinkNode(decisionTreeId: string): DecisionTreeLinkNode {
  return new DecisionTreeLinkNode(decisionTreeId);
}

export function $isDecisionTreeLinkNode(
  node: DecisionTreeLinkNode | LexicalNode | null | undefined,
): node is DecisionTreeLinkNode {
  return node instanceof DecisionTreeLinkNode;
}
